import { axiosJsonDirectInstance, axiosJsonInstance } from "@/axios.instance";
import { Guard, GuardCheckInPayload, GuardCheckInResponse, GuardTourCheckInPayload, DispatchRequest, MobileGuardsRequest, RaiseDuressEventResponse, RaiseDuressEvent } from "@/store/mobile/types";
import { AxiosInstance, AxiosResponse } from "axios";

class MobileApi {
	private axios: AxiosInstance;
	private axionJsonDirectInstance: AxiosInstance;

	constructor() {
		this.axios = axiosJsonInstance;
		this.axionJsonDirectInstance = axiosJsonDirectInstance;
	}

	// Mobile
	public async extendMobileSession(): Promise<any> {
		await this.axios.post("/Mobile/Session/Extend");
	}

	public async registerUserDevice(deviceID: string, deviceTypeID: number): Promise<any> {
		const userDevicePayload = {
			DeviceID: deviceID,
			DeviceType: deviceTypeID
		};

		await this.axios.post("/Mobile/Device/Register", userDevicePayload);
	}

	public async removeUserFromGuard(guardId: number) {
		const response = await this.axios.post(`/DispatchGuard/Session/RemoveUserFromGuard`, guardId);
		return response && response.data ? response.data : null;
	}

	public async fetchAuditEndpoint(): Promise<any> {
		const response = await this.axios.get(`/Mobile/FetchAuditEndpoint`);
		return response && response.data ? response.data : null;
	}

	// Mobile Dispatch Control in Site Monitor
	public async loadMobileGuardsForEvent(mobileGuardsRequest: MobileGuardsRequest): Promise<Guard[]> {
		let eventID = mobileGuardsRequest.EventID;
		let location = mobileGuardsRequest.Location;
		let includeGuardsWithMatchingFilter = mobileGuardsRequest.IncludeGuardsWithMatchingFilter;

		const response = await this.axios.get(`/Dispatch/MobileGuardsForEvent`, {
			params: {
				eventID,
				location,
				includeGuardsWithMatchingFilter
			}
		});
		return response && response.data ? response.data : [];
	}

	// Mobile Dispatch Guard
	public async setLocationForGuard(location: string) {
		await this.axios.post("/DispatchGuard/Session/SetLocation", location);
	}

	public async mobileGuardLogout(guardID: number, deviceID: string, deviceTypeID: number): Promise<any> {
		const logoutPayload = {
			GuardID: guardID,
			UserDevice: {
				DeviceID: deviceID,
				DeviceType: deviceTypeID
			}
		};

		await this.axios.post("/DispatchGuard/Session/Logout", logoutPayload);
	}

	public async addEventRecordForGuard(
		eventId: number,
		details: string,
		objectId: number,
		eventRecordTypeId: number
	): Promise<any> {
		const eventRecordPayload = {
			EventID: eventId,
			Details: details,
			ObjectID: objectId,
			EventRecordTypeID: eventRecordTypeId
		};

		const response = await this.axios.post(`/DispatchGuard/EventRecord/Add`, eventRecordPayload);
		return response && response.data ? response.data : null;
	}

	public async loadCallSigns(): Promise<any> {
		const response = await this.axios.get("/DispatchGuard/CallSign/LoadAll");
		return response && response.data ? response.data : null;
	}

	public async loadCoverCallSigns(): Promise<AxiosResponse<Guard[] | null>> {
		return this.axios.get("/DispatchGuard/CallSign/LoadCoverable");
	}

	public async requestCover(coverGuardId): Promise<void> {
		await this.axios.post(`/DispatchGuard/CoverRequest/${coverGuardId}`);
	}

	public async rejectCover(coverGuardId): Promise<void> {
		await this.axios.post(`/DispatchGuard/CoverRequest/Reject/${coverGuardId}`);
	}

	public async acceptCover(coverGuardId): Promise<void> {
		await this.axios.post(`/DispatchGuard/CoverRequest/Accept/${coverGuardId}`);
	}

	async cancelCover(): Promise<void> {
		await this.axios.delete("DispatchGuard/CoverRequest/Cancel");
	}

	async endCover(): Promise<AxiosResponse<Guard>> {
		return this.axios.post("DispatchGuard/CoverRequest/End");
	}

	public async loadCoverRequest(): Promise<AxiosResponse<Guard | null>> {
		return this.axios.get("/DispatchGuard/CoverRequest");
	}

	public async setGuardForUser(guardId: number): Promise<any> {
		const response = await this.axios.post(`/DispatchGuard/Session/SetGuardForUser`, guardId);
		return response && response.data ? response.data : null;
	}

	public async loadGuardForUser(): Promise<Guard | null> {
		const response = await this.axios.get(`/DispatchGuard/Session/GetGuardForUser`);
		return response && response.data ? response.data : null;
	}

	public async loadGuardStatus(guardId: number): Promise<any> {
		const response = await this.axios.get(`/DispatchGuard/Status/`, {
			params: {
				guardId
			}
		});
		return response && response.data ? response.data : null;
	}

	public async loadUnavailableStatuses(): Promise<any> {
		const response = await this.axios.get(`/DispatchGuard/UnavailableStatus/LoadAll`);
		return response && response.data ? response.data : null;
	}

	public async setUnavailableStatus(
		guardId: number,
		unavailableStatusId: number,
		unavailableReason: string
	): Promise<any> {
		const unavailableStatusPayload = {
			GuardId: guardId,
			UnavailableStatusId: unavailableStatusId,
			UnavailableReason: unavailableReason
		};

		await this.axios.post(`/DispatchGuard/UnavailableStatus/Set`, unavailableStatusPayload);
	}

	public async loadDispatchRequestsForGuard(GuardID: number): Promise<any> {
		const response = await this.axios.get(`/DispatchGuard/DispatchRequest/LoadAll`, {
			params: {
				GuardID
			}
		});
		return response && response.data ? response.data : null;
	}

	public async claimDispatchRequest(dispatchQueueId: number, guardId: number): Promise<any> {
		const claimPayload = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};

		await this.axios.post(`/DispatchGuard/DispatchRequest/Claim`, claimPayload);
	}

	public async unclaimDispatchRequest(dispatchQueueId: number, guardId: number): Promise<any> {
		const unclaimPayload = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};

		await this.axios.post(`/DispatchGuard/DispatchRequest/Unclaim`, unclaimPayload);
	}

	public async getGuardTourByEvent(eventId: number): Promise<any> {
		return await this.axios.get(`/GuardTour/getGuardTourByEvent/${eventId}`);
	}

	public async activateDispatchRequest(dispatchQueueId: number, guardId: number): Promise<any> {
		const activatePayload = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};

		await this.axios.post(`/DispatchGuard/DispatchRequest/Activate`, activatePayload);
	}

	public async inactivateDispatchRequest(dispatchQueueId: number, guardId: number): Promise<any> {
		const inactivatePayload = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};

		await this.axios.post(`/DispatchGuard/DispatchRequest/Inactivate`, inactivatePayload);
	}

	public async guardArrived(dispatchQueueId: number, guardId: number): Promise<void> {
		const statusPayload = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};

		await this.axios.post(`/DispatchGuard/Arrived`, statusPayload);
	}

	public async guardAvailable(guardId: number): Promise<any> {
		await this.axios.post(`/DispatchGuard/Available`, guardId);
	}

	public async dispatchedGuardCheckIn(
		checkInPayload: GuardCheckInPayload
	): Promise<AxiosResponse<GuardCheckInResponse>> {
		return this.axionJsonDirectInstance.post("/DispatchGuard/DispatchRequest/CheckInLocation", checkInPayload);
	}

	public async guardTourStepCheckIn(
		checkInPayload: GuardTourCheckInPayload
	): Promise<AxiosResponse<GuardCheckInResponse>> {
		return this.axionJsonDirectInstance.post("/GuardTour/CheckGuardIntoLocation", checkInPayload);
	}

	public async getTourStepLocationId(tourStepId: number): Promise<string> {
		return (await this.axios.get(`/GuardTour/GetTourStepLocationId/${tourStepId}`)).data;
	}

	public async addDispatchRequest(
		preferredGuardId: number,
		patrolZoneId: number,
		eventId: number,
		requester: string,
		requesterDescription: string,
		location: string,
		siteTitle: string
	): Promise<any> {
		const dispatchRequestPayload = {
			PreferredGuardID: preferredGuardId,
			PatrolZoneID: patrolZoneId,
			EventID: eventId,
			Requester: requester,
			RequesterDescription: requesterDescription,
			Location: location,
			SiteTitle: siteTitle
		};

		return await this.axios.post("/DispatchGuard/DispatchRequest/Add", dispatchRequestPayload);
	}

	public async dispatchGuardToTour(dispatchRequest: Partial<DispatchRequest>): Promise<any> {
		return await this.axios.post("GuardTour/DispatchGuardToTour", dispatchRequest, {
			params: {
				guardTourId: dispatchRequest.guardTourId
			}
		});
	}

	public async finishDispatchRequest(dispatchQueueId: number, guardId: number): Promise<any> {
		const finishPayload = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};
		await this.axios.post(`/DispatchGuard/DispatchRequest/Finish`, finishPayload);
	}

	public async continueTour(tourId: number, stepId: number, dispatchQueueId: number, guardId: number): Promise<any> {
		const dispatchRequestIdentifier = {
			DispatchQueueId: dispatchQueueId,
			GuardId: guardId
		};
		await this.axios.post(`/GuardTour/ContinueTour/${tourId}/${stepId}`, dispatchRequestIdentifier);
	}

	public async setAssesmentTourResult(dispatchQueueId: number, result: boolean): Promise<any> {
		const payload = {
			DispatchQueueId: dispatchQueueId,
			Result: result
		};

		await this.axios.post(`/DispatchGuard/SetAssesmentTourResult`, payload);
	}

	public async loadNotesForEvent(eventId: number): Promise<any> {
		const response = await this.axios.get(`/DispatchGuard/Notes/ForEvent`, {
			params: {
				eventId
			}
		});
		return response && response.data ? response.data : null;
	}

	public async addNoteForEvent(guardId: number, guardNote: string, eventId: number): Promise<any> {
		const guardNotePayload = {
			GuardId: guardId,
			GuardNote: guardNote,
			EventId: eventId
		};

		await this.axios.post(`/DispatchGuard/Notes/Add`, guardNotePayload);
	}

	// Starred Devices for user.
	public async toggleDeviceStarred(deviceID: number) {
		await this.axios.post("/MobileLiveCameras/Device/ToggleStar/", {
			DeviceId: deviceID
		});
	}

	public async getAllStarredDevices() {
		let devices = await this.axios.get("/MobileLiveCameras/Device/GetAllStarredDevices");
		return devices.data;
	}

	public async getQuickCloseOutcomes() {
		let outcomes = await this.axios.get("/DispatchGuard/QuickClose/GetAll");
		return outcomes.data;
	}

	public async loadPatrolZones() {
		let patrolZones = await this.axios.get("/DispatchGuard/PatrolZones/LoadAll");
		return patrolZones.data;
	}

	public async assignIncidentCommander(guardId: number, dispatchQueueId: number) {
		await this.axios.post("/DispatchGuard/IncidentCommander/Assign", {
			guardId,
			dispatchQueueId
		});
	}

	public async getEventIdsForUser(): Promise<number[]> {
		const { data } = await this.axios.get("/Messages/GetEventsForUser");
		return data;
	}

	public async getSocTelephone(): Promise<string> {
		return (await this.axios.get("Soc/GetSOCTelephone")).data;
	}

	public auditSocCall() {
		this.axios.get("Soc/TrackOutgoingCall");
	}

    public async raiseDuressEvent(raiseDuressEvent: RaiseDuressEvent): Promise<AxiosResponse<RaiseDuressEventResponse>> {
        return await this.axios.post("/MobileDuress", raiseDuressEvent);
    }
}

const mobileApi = new MobileApi();
export default mobileApi;
